import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface CashflowItem {
  date: string;
  principal: number;
  interest: number;
  fees: number;
  total: number;
  cumulative: number;
}

interface CashflowApiItem {
  date: string;
  principalIn: number;
  interestIn: number;
  feesIn: number;
  totalIn: number;
  cumulativeCashflow: number;
}

export interface NPVSummary {
  npv: number | null;
  discountRate: number | null;
  totalFutureCashflows: number;
  cashflowCount: number;
  wal: number;
  loanPrincipal?: number;
  npvToParRatio: number | null;
}

export interface YieldSummary {
  allInYield: number;
  fundingCost: number;
  nim: number;
}

interface YieldApiSummary {
  allInYield: number;
  fundingCostRate: number;
  netInterestMargin: number;
}

function unwrapListResponse<T>(
  payload: T[] | { data?: T[] | null; items?: T[] | null; results?: T[] | null } | null | undefined,
): T[] {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== 'object') {
    return [];
  }

  if (Array.isArray(payload.data)) {
    return payload.data;
  }

  if (Array.isArray(payload.items)) {
    return payload.items;
  }

  if (Array.isArray(payload.results)) {
    return payload.results;
  }

  return [];
}

function unwrapObjectResponse<T>(payload: T | { data?: T | null } | null | undefined): T | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  if ('data' in payload && payload.data && typeof payload.data === 'object') {
    return payload.data as T;
  }

  return payload as T;
}

export function useLoanCashflows(loanId: string) {
  return useQuery({
    queryKey: ['loans', loanId, 'cashflows'],
    queryFn: async () => {
      const { data } = await api.get<CashflowApiItem[]>(`/loans/${loanId}/cashflows`);
      return unwrapListResponse<CashflowApiItem>(data).map(item => ({
        date: item.date,
        principal: Number(item.principalIn ?? 0),
        interest: Number(item.interestIn ?? 0),
        fees: Number(item.feesIn ?? 0),
        total: Number(item.totalIn ?? 0),
        cumulative: Number(item.cumulativeCashflow ?? 0),
      }));
    },
    enabled: !!loanId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLoanNPV(loanId: string, discountRate?: number) {
  return useQuery({
    queryKey: ['loans', loanId, 'npv', discountRate],
    queryFn: async () => {
      const params = discountRate !== undefined ? { discountRate } : {};
      const { data } = await api.get<NPVSummary>(`/loans/${loanId}/npv`, { params });
      const payload = unwrapObjectResponse<NPVSummary>(data);

      return payload
        ? {
            npv: payload.npv ?? null,
            discountRate: payload.discountRate ?? null,
            totalFutureCashflows: Number(payload.totalFutureCashflows ?? 0),
            cashflowCount: Number(payload.cashflowCount ?? 0),
            wal: Number(payload.wal ?? 0),
            loanPrincipal: payload.loanPrincipal,
            npvToParRatio: payload.npvToParRatio ?? null,
          }
        : {
            npv: null,
            discountRate: discountRate ?? null,
            totalFutureCashflows: 0,
            cashflowCount: 0,
            wal: 0,
            loanPrincipal: 0,
            npvToParRatio: null,
          };
    },
    enabled: !!loanId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLoanYield(loanId: string) {
  return useQuery({
    queryKey: ['loans', loanId, 'yield'],
    queryFn: async () => {
      const { data } = await api.get<YieldApiSummary>(`/loans/${loanId}/yield`);
      const payload = unwrapObjectResponse<YieldApiSummary>(data);

      return payload
        ? {
            allInYield: Number(payload.allInYield ?? 0),
            fundingCost: Number(payload.fundingCostRate ?? 0),
            nim: Number(payload.netInterestMargin ?? 0),
          }
        : {
            allInYield: 0,
            fundingCost: 0,
            nim: 0,
          };
    },
    enabled: !!loanId,
    staleTime: 5 * 60 * 1000,
  });
}
